import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * UI Slice
 * Manages UI state like sidebar, modals, notifications, etc.
 *
 * @author Civilhealth Development Team
 */

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  title?: string;
  duration?: number;
}

interface UIState {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark';
  pageTitle: string;
  pageBreadcrumb: { label: string; path?: string }[];
  loading: boolean;
  notifications: Notification[];
  modal: {
    open: boolean;
    type: string | null;
    data: any;
  };
  drawer: {
    open: boolean;
    type: string | null;
    data: any;
  };
  confirmDialog: {
    open: boolean;
    title: string;
    message: string;
    onConfirm?: () => void;
    onCancel?: () => void;
  };
}

const initialState: UIState = {
  sidebarOpen: true,
  sidebarCollapsed: false,
  theme: 'light',
  pageTitle: '',
  pageBreadcrumb: [],
  loading: false,
  notifications: [],
  modal: {
    open: false,
    type: null,
    data: null,
  },
  drawer: {
    open: false,
    type: null,
    data: null,
  },
  confirmDialog: {
    open: false,
    title: '',
    message: '',
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Sidebar
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleSidebarCollapse: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },

    // Theme
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
      localStorage.setItem('theme', action.payload);
    },

    // Page
    setPageTitle: (state, action: PayloadAction<string>) => {
      state.pageTitle = action.payload;
    },
    setPageBreadcrumb: (state, action: PayloadAction<{ label: string; path?: string }[]>) => {
      state.pageBreadcrumb = action.payload;
    },

    // Loading
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Notifications
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },

    // Modal
    openModal: (state, action: PayloadAction<{ type: string; data?: any }>) => {
      state.modal.open = true;
      state.modal.type = action.payload.type;
      state.modal.data = action.payload.data || null;
    },
    closeModal: (state) => {
      state.modal.open = false;
      state.modal.type = null;
      state.modal.data = null;
    },

    // Drawer
    openDrawer: (state, action: PayloadAction<{ type: string; data?: any }>) => {
      state.drawer.open = true;
      state.drawer.type = action.payload.type;
      state.drawer.data = action.payload.data || null;
    },
    closeDrawer: (state) => {
      state.drawer.open = false;
      state.drawer.type = null;
      state.drawer.data = null;
    },

    // Confirm Dialog
    openConfirmDialog: (
      state,
      action: PayloadAction<{
        title: string;
        message: string;
        onConfirm?: () => void;
        onCancel?: () => void;
      }>
    ) => {
      state.confirmDialog.open = true;
      state.confirmDialog.title = action.payload.title;
      state.confirmDialog.message = action.payload.message;
    },
    closeConfirmDialog: (state) => {
      state.confirmDialog.open = false;
      state.confirmDialog.title = '';
      state.confirmDialog.message = '';
    },

    // Success notification helper
    showSuccess: (state, action: PayloadAction<string>) => {
      const notification: Notification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'success',
        message: action.payload,
        duration: 5000,
      };
      state.notifications.push(notification);
    },

    // Error notification helper
    showError: (state, action: PayloadAction<string>) => {
      const notification: Notification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'error',
        message: action.payload,
        duration: 8000,
      };
      state.notifications.push(notification);
    },

    // Warning notification helper
    showWarning: (state, action: PayloadAction<string>) => {
      const notification: Notification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'warning',
        message: action.payload,
        duration: 6000,
      };
      state.notifications.push(notification);
    },

    // Info notification helper
    showInfo: (state, action: PayloadAction<string>) => {
      const notification: Notification = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        type: 'info',
        message: action.payload,
        duration: 5000,
      };
      state.notifications.push(notification);
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  toggleSidebarCollapse,
  setSidebarCollapsed,
  setTheme,
  setPageTitle,
  setPageBreadcrumb,
  setLoading,
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  openDrawer,
  closeDrawer,
  openConfirmDialog,
  closeConfirmDialog,
  showSuccess,
  showError,
  showWarning,
  showInfo,
} = uiSlice.actions;

export default uiSlice.reducer;